import { useEffect, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import PrintIcon from "@mui/icons-material/Print";
import { colorPalette } from "../styles/theme";
import { wsApi } from "../api/services";
import SurveyPrintDocument from "./SurveyPrintDocument";

/** 만족도 조사 assessment ID (고정값) */
const ASSESSMENT_ID = 6;

// ─── 스타일 상수 ────────────────────────────────────────────────
const CATEGORY_COLORS = {
  "기본 정보": "#6366f1",
  "운영·강사 만족도": "#0ea5e9",
  "도움이 된 프로그램": "#10b981",
  "프로그램 효과성": "#f59e0b",
  "주관식 의견": "#8b5cf6",
};

const PERIOD_COLORS = {
  전체: { bg: "#2d6cdf", light: "#2d6cdf18", border: "#2d6cdf30" },
  단기: { bg: "#10b981", light: "#10b98118", border: "#10b98130" },
  중기: { bg: "#f59e0b", light: "#f59e0b18", border: "#f59e0b30" },
  장기: { bg: "#8b5cf6", light: "#8b5cf618", border: "#8b5cf630" },
};

const LIKERT_COLORS = ["#2d6cdf", "#38bdf8", "#94a3b8", "#fb923c", "#ef4444"];
const DEFAULT_COLORS = ["#2d6cdf", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444"];

const getBarColor = (index, totalOptions) =>
  totalOptions === 5
    ? LIKERT_COLORS[index] ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length]
    : DEFAULT_COLORS[index % DEFAULT_COLORS.length];

const getPeriodColor = (periodName = "") => {
  if (PERIOD_COLORS[periodName]) return PERIOD_COLORS[periodName];

  const matchedKey = Object.keys(PERIOD_COLORS).find((key) =>
    periodName.startsWith(key),
  );

  return PERIOD_COLORS[matchedKey] ?? PERIOD_COLORS["전체"];
};

// ─── 서브 컴포넌트 ───────────────────────────────────────────────

/** 가로 퍼센트 바 */
const OptionBar = ({ option, index, totalOptions }) => {
  const barColor = getBarColor(index, totalOptions);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <Typography variant="body2" sx={{ color: colorPalette.textSecondary, fontSize: 13 }}>
          {option.content}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ fontSize: 12, color: colorPalette.textSecondary }}>
            {option.count}명
          </Typography>
          <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 700, color: barColor, minWidth: 40, textAlign: "right" }}>
            {option.percentage}%
          </Typography>
        </Box>
      </Box>
      <Box sx={{ height: 8, borderRadius: 4, backgroundColor: "#f1f2f6", overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${option.percentage}%`, backgroundColor: barColor, borderRadius: 4, transition: "width 0.6s ease" }} />
      </Box>
    </Box>
  );
};

/** 질문 카드 */
const QuestionCard = ({ question, questionIndex }) => {
  const isChoice =
    question.question_type === "SINGLE_CHOICE" ||
    question.question_type === "MULTIPLE_CHOICE" ||
    (question.options && question.options.length > 0);
  const isText =
    question.question_type === "TEXT" ||
    (!isChoice && Array.isArray(question.text_answers));

  const typeLabel = {
    SINGLE_CHOICE: "단일 선택",
    MULTIPLE_CHOICE: "복수 선택",
    TEXT: "주관식",
  }[question.question_type] ?? (isChoice ? "선택형" : "주관식");

  const typeColor = {
    SINGLE_CHOICE: "#2d6cdf",
    MULTIPLE_CHOICE: "#10b981",
    TEXT: "#8b5cf6",
  }[question.question_type] ?? "#64748b";

  return (
    <Paper sx={{ p: 2.5, backgroundColor: colorPalette.white, border: `1px solid ${colorPalette.border}` }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
        <Box sx={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: colorPalette.background, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: 0.2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: colorPalette.textSecondary, fontSize: 11 }}>
            {questionIndex + 1}
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: colorPalette.textPrimary, fontSize: 14, lineHeight: 1.5 }}>
            {question.content}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 0.5, alignItems: "center", flexWrap: "wrap" }}>
            <Chip label={typeLabel} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600, backgroundColor: `${typeColor}18`, color: typeColor, border: `1px solid ${typeColor}40` }} />
            <Typography variant="caption" sx={{ color: colorPalette.textSecondary }}>
              응답 {question.total_responses}건
            </Typography>
            {question.average_score != null && (
              <Chip label={`평균 ${question.average_score}점`} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, backgroundColor: "#fef3c7", color: "#d97706" }} />
            )}
          </Box>
        </Box>
      </Box>

      {isChoice && question.options.length > 0 && (
        <Box>
          {question.options.map((opt, idx) => (
            <OptionBar key={`${opt.content}-${idx}`} option={opt} index={idx} totalOptions={question.options.length} />
          ))}
        </Box>
      )}

      {isText && (
        <Box>
          {!question.text_answers || question.text_answers.length === 0 ? (
            <Typography variant="body2" sx={{ color: "#aaa", fontStyle: "italic" }}>제출된 답변이 없습니다.</Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {question.text_answers.map((answer, idx) => (
                <Box key={idx} sx={{ p: 1.5, backgroundColor: colorPalette.background, borderRadius: 2, borderLeft: "3px solid #8b5cf6" }}>
                  <Typography variant="body2" sx={{ color: colorPalette.textPrimary, fontSize: 13, lineHeight: 1.6 }}>
                    {typeof answer === "string" ? answer : answer.answer ?? JSON.stringify(answer)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

/** 카테고리 섹션 */
const CategorySection = ({ category, questions }) => {
  const color = CATEGORY_COLORS[category] ?? colorPalette.primary;
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box sx={{ width: 4, height: 20, borderRadius: 2, backgroundColor: color }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colorPalette.textPrimary }}>
          {category}
        </Typography>
        <Chip label={`${questions.length}개`} size="small" sx={{ height: 20, fontSize: 11, backgroundColor: `${color}15`, color, fontWeight: 600 }} />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, ml: 1 }}>
        {questions.map((q, idx) => (
          <QuestionCard key={q.question_id} question={q} questionIndex={idx} />
        ))}
      </Box>
    </Box>
  );
};

/** 기간 탭 통계 뷰 */
const PeriodStats = ({ totalParticipants, questions, periodColor }) => {
  const grouped = useMemo(() => {
    const orderMap = {};
    const order = [];
    questions.forEach((q) => {
      const cat = q.category ?? "기타";
      if (!orderMap[cat]) { orderMap[cat] = []; order.push(cat); }
      orderMap[cat].push(q);
    });
    return order.map((cat) => ({ category: cat, questions: orderMap[cat] }));
  }, [questions]);

  return (
    <Box>
      <Paper sx={{ p: 2.5, mb: 3, background: `linear-gradient(135deg, ${periodColor.light} 0%, ${periodColor.light} 100%)`, border: `1px solid ${periodColor.border}` }}>
        <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PeopleAltIcon sx={{ fontSize: 20, color: periodColor.bg }} />
            <Box>
              <Typography variant="caption" sx={{ color: colorPalette.textSecondary, display: "block" }}>응답자 수</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: periodColor.bg, lineHeight: 1.2 }}>{totalParticipants}명</Typography>
            </Box>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FormatListBulletedIcon sx={{ fontSize: 20, color: colorPalette.textSecondary }} />
            <Box>
              <Typography variant="caption" sx={{ color: colorPalette.textSecondary, display: "block" }}>질문 수</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colorPalette.textPrimary, lineHeight: 1.2 }}>{questions.length}개</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {grouped.map(({ category, questions: qs }) => (
          <CategorySection key={category} category={category} questions={qs} />
        ))}
      </Box>
    </Box>
  );
};

// ─── 메인 컴포넌트 ────────────────────────────────────────────────

/**
 * "YYYY-MM-DD" 형태의 날짜 문자열을 "YYYY년 M월 D일" 형식으로 변환합니다.
 * @param {string|null} dateStr
 * @returns {string}
 */
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
};

/**
 * "만족도 조사 통계" 뷰
 * [PDF 출력] 버튼 클릭 → window.print() → @media print CSS로 SurveyPrintDocument만 인쇄
 */
const SurveyStatsView = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setLoading(true);
    wsApi
      .getSatisfactionSurveyStats(ASSESSMENT_ID)
      .then((res) => { 
        setStats(res.data); 
      })
      .catch((err) => {
        // console.error("[SurveyStatsView] API 호출 실패:", err);
        setError("통계 데이터를 불러오는 중 오류가 발생했습니다.");
      })
      .finally(() => { setLoading(false); });
  }, []);

  // 서버가 내려준 기간 목록을 그대로 탭으로 사용합니다.
  const periodTabs = useMemo(() => {
    const periods = stats?.by_period?.map((p) => p.period).filter(Boolean) ?? [];
    return periods;
  }, [stats]);

  useEffect(() => {
    if (activeTab >= periodTabs.length) {
      setActiveTab(0);
    }
  }, [activeTab, periodTabs.length]);

  // 현재 탭 데이터
  const currentData = useMemo(() => {
    if (!stats) return null;
    const periodName = periodTabs[activeTab];
    return stats.by_period?.find((p) => p.period === periodName) ?? null;
  }, [stats, activeTab, periodTabs]);

  /**
   * 브라우저 기본 인쇄 다이얼로그를 엽니다.
   * @media print CSS에 의해 .survey-print-root만 표시되고
   * .no-print 요소(화면 UI)는 모두 숨겨집니다.
   */
  const handlePrint = () => {
    window.print();
  };

  // ── 로딩 ──
  if (loading) {
    return (
      <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={36} sx={{ mb: 2 }} />
          <Typography variant="body2" sx={{ color: colorPalette.textSecondary }}>
            통계 데이터를 불러오는 중...
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── 에러 ──
  if (error) {
    return (
      <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body2" sx={{ color: colorPalette.error }}>{error}</Typography>
      </Box>
    );
  }

  if (!stats) return null;

  const currentPeriodName = periodTabs[activeTab] ?? "전체";
  const tabColor = getPeriodColor(currentPeriodName);

  return (
    <>
      {/* 화면 UI — no-print 클래스로 인쇄 시 숨김 */}
      <Box className="no-print" sx={{ display: "flex", flex: 1, flexDirection: "column", overflow: "hidden" }}>
        {/* 헤더 (제목 + 수집 기간 + PDF 버튼 + 탭) */}
        <Paper sx={{ px: 3, pt: 2.5, pb: 0, mb: 2, flexShrink: 0, borderBottom: `1px solid ${colorPalette.border}` }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
            {/* 제목 + 수집 기간 */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: colorPalette.textPrimary }}>
                {stats.title}
              </Typography>
              {/* 수집 기간 (start_date / end_date 가 있을 때만 표시) */}
              {(stats.start_date || stats.end_date) && (
                <Typography
                  variant="caption"
                  sx={{ color: colorPalette.textSecondary, display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}
                >
                  📅 수집 기간&nbsp;
                  <strong style={{ color: colorPalette.textPrimary }}>
                    {formatDate(stats.start_date)}
                    {stats.start_date && stats.end_date ? " ~ " : ""}
                    {formatDate(stats.end_date)}
                  </strong>
                </Typography>
              )}
            </Box>
            {/* 인쇄(PDF 저장) 버튼 */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                borderColor: "#ef4444",
                color: "#ef4444",
                "&:hover": { borderColor: "#dc2626", backgroundColor: "#fef2f2" },
              }}
            >
              PDF 출력
            </Button>
          </Box>
          <Tabs
            value={activeTab}
            onChange={(_, newVal) => setActiveTab(newVal)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": { fontSize: 13, fontWeight: 600, minWidth: 80, px: 2, textTransform: "none", color: colorPalette.textSecondary },
              "& .Mui-selected": { color: `${tabColor.bg} !important` },
              "& .MuiTabs-indicator": { backgroundColor: tabColor.bg, height: 3, borderRadius: "3px 3px 0 0" },
            }}
          >
            {periodTabs.map((label) => (
              <Tab key={label} label={label} />
            ))}
          </Tabs>
        </Paper>

        {/* 탭 컨텐츠 */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 0.5 }}>
          {currentData ? (
            <PeriodStats
              totalParticipants={currentData.total_participants}
              questions={currentData.questions ?? []}
              periodColor={tabColor}
            />
          ) : (
            <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center", height: "100%" }}>
              <Typography variant="body2" sx={{ color: colorPalette.textSecondary }}>
                해당 기간의 데이터가 없습니다.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* 인쇄 전용 레이아웃 — 현재 탭 기간 데이터만 출력, 화면에선 숨김 */}
      <SurveyPrintDocument
        stats={stats}
        periodName={currentPeriodName}
        periodData={currentData}
      />
    </>
  );
};

export default SurveyStatsView;
